import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Ticket, Phone, Search, X, ArrowRight } from "lucide-react";
import { findActiveTokenByPhone, getTokenByNumber } from "../lib/api";
import { getLastToken, clearLastToken, saveLastToken } from "../lib/lastToken";

// "Already booked today?" lookup card — sits at the top of the booking page.
//
// Flow:
//   1. If localStorage has today's last token for THIS shop AND that token is
//      still active (waiting/serving), go straight to its ticket.
//   2. Otherwise, open a small phone-number modal. Lookup by digit-suffix match
//      against today's waiting/serving tokens. If found, navigate; if not, toast.

export default function ResumeTokenCard({ shop }) {
  const navigate = useNavigate();
  const [showLookup, setShowLookup] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onResumeClick() {
    if (busy) return;
    setBusy(true);
    try {
      // 1. Local fast-path
      const local = getLastToken(shop.slug);
      if (local?.tokenNumber) {
        // Verify the token still exists & is active today before redirecting.
        const tok = await getTokenByNumber(shop.id, local.tokenNumber);
        if (tok && (tok.status === "waiting" || tok.status === "serving")) {
          navigate(`/s/${shop.slug}/ticket/${tok.token_number}`);
          return;
        }
        // Stale — drop it and fall through to the phone-number prompt.
        clearLastToken(shop.slug);
      }
      // 2. Ask for phone number
      setShowLookup(true);
    } catch (err) {
      toast.error(err?.message || "Could not resume token");
    } finally {
      setBusy(false);
    }
  }

  async function lookupByPhone(phone) {
    const trimmed = String(phone || "").trim();
    if (!trimmed) {
      toast.error("Please enter your mobile number");
      return false;
    }
    const found = await findActiveTokenByPhone(shop.id, trimmed);
    if (!found) {
      toast.error("No active token found. Please book a new token.");
      return false;
    }
    // Save it locally so the next visit auto-opens
    saveLastToken(shop.slug, {
      tokenNumber: found.token_number,
      whatsapp: found.whatsapp,
    });
    navigate(`/s/${shop.slug}/ticket/${found.token_number}`);
    return true;
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="mt-8 rounded-2xl p-5 md:p-6 relative overflow-hidden border border-gold/30"
        style={{
          background:
            "linear-gradient(180deg, rgba(35,28,12,0.55) 0%, rgba(18,18,18,0.85) 100%)",
          backdropFilter: "blur(20px) saturate(140%)",
          WebkitBackdropFilter: "blur(20px) saturate(140%)",
          boxShadow:
            "inset 0 1px 0 rgba(230,198,98,0.18), 0 8px 32px -10px rgba(230,198,98,0.25)",
        }}
        data-testid="resume-token-card"
      >
        <div className="absolute -top-24 -right-16 w-48 h-48 rounded-full bg-gold/15 blur-3xl pointer-events-none" />

        <div className="relative flex items-start gap-4">
          <div className="hidden sm:flex w-12 h-12 rounded-xl border border-gold/40 bg-void/60 items-center justify-center gold-glow shrink-0">
            <Ticket className="w-5 h-5 text-gold" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-numbers text-[11px] tracking-[0.35em] uppercase text-gold">
              Already booked today?
            </div>
            <div className="font-heading text-xl md:text-2xl mt-1 text-white leading-snug">
              Continue without booking again
            </div>
            <p className="mt-1 text-sm text-white/65 leading-relaxed">
              If you have already taken a token today, open your live ticket and
              skip the form.
            </p>
            <button
              type="button"
              onClick={onResumeClick}
              disabled={busy}
              data-testid="resume-token-button"
              className="btn-gold mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 text-base py-3 px-5"
            >
              <Search className="w-4 h-4" />
              {busy ? "Checking…" : "View My Live Token"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showLookup && (
          <PhoneLookupModal
            onClose={() => setShowLookup(false)}
            onSubmit={lookupByPhone}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function PhoneLookupModal({ onClose, onSubmit }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    try {
      const ok = await onSubmit(phone);
      if (ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm rounded-2xl border border-gold/30 p-6 relative"
        style={{
          background:
            "linear-gradient(180deg, rgba(22,22,22,0.96) 0%, rgba(12,12,12,0.96) 100%)",
          boxShadow:
            "inset 0 1px 0 rgba(230,198,98,0.15), 0 20px 60px -12px rgba(0,0,0,0.6)",
        }}
        data-testid="phone-lookup-modal"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-gold hover:bg-white/5"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="font-numbers text-[11px] tracking-[0.35em] uppercase text-gold">
          Find My Token
        </div>
        <div className="font-heading text-2xl mt-1 text-white">
          Enter your mobile number
        </div>
        <p className="mt-1 text-sm text-white/60">
          We&apos;ll look up today&apos;s active token for you.
        </p>

        <div className="mt-5">
          <label
            htmlFor="lookup-phone"
            className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/55"
          >
            WhatsApp / Mobile
          </label>
          <div className="relative">
            <Phone
              className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-gold/80"
              strokeWidth={1.5}
            />
            <input
              id="lookup-phone"
              autoFocus
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              data-testid="lookup-phone-input"
              className="input-bright mt-2 pl-7 text-base md:text-lg"
              placeholder="+91 98765 43210"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={busy || phone.trim().length < 6}
          data-testid="lookup-submit"
          className="btn-gold w-full mt-6 inline-flex items-center justify-center gap-2 text-base py-3.5"
        >
          <Search className="w-5 h-5" />
          {busy ? "Searching…" : "Find My Token"}
        </button>

        <div className="mt-3 text-[11px] text-white/45 text-center font-numbers tracking-[0.2em] uppercase">
          We only search today&apos;s active tokens
        </div>
      </motion.form>
    </motion.div>
  );
}
